import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FiUsers, FiX, FiCalendar, FiSearch, FiRefreshCcw, FiUserPlus, FiUserMinus } from 'react-icons/fi';
import { fetchSchuljahre, fetchKlassen, fetchLernendeBySchuljahrId, fetchLernendeByKlassenId, fetchTaskAssignments } from '../api';
import { usePlanMetadata } from '../PlanMetadataContext';
import { useLoginContext } from '../LoginContext';

// Spinner-Komponente
const Spinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '40px',
    width: '100%'
  }}>
    <div style={{
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #007acc',
      borderRadius: '50%',
      width: '32px',
      height: '32px',
      animation: 'spin 1s linear infinite'
    }} />
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

const SchuelerModal = ({
  show,
  onClose,
  onUebernehmen = null,
  existingStudents = [], // Prop für bereits zugewiesene Lernende (Fallback)
  currentTaskData = null // Task-Daten um Zuordnungen aus DB zu laden
}) => {
  const [schuljahre, setSchuljahre] = useState([]);
  const [selectedSchuljahr, setSelectedSchuljahr] = useState('');
  const [klassen, setKlassen] = useState([]);
  const [lernende, setLernende] = useState([]);
  const [selectedLernende, setSelectedLernende] = useState(new Set());
  const [searchVorname, setSearchVorname] = useState('');
  const [searchNachname, setSearchNachname] = useState('');
  const [selectedKlasse, setSelectedKlasse] = useState('');
  const [loading, setLoading] = useState(false);
  const [klassesLoading, setKlassesLoading] = useState(false);
  const [lernendeLoading, setLernendeLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [submitError, setSubmitError] = useState('');
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [actionMode, setActionMode] = useState('add'); // 'add' oder 'remove'
  const [dbAssignedStudents, setDbAssignedStudents] = useState([]); // Aus DB geladene Lernende
  const [loadingAssignedStudents, setLoadingAssignedStudents] = useState(false);

  // Context für Benutzerdaten und Plan-Metadaten
  const { user } = useLoginContext();
  const { planMetadata } = usePlanMetadata();

  // Lade zugewiesene Lernende aus der Datenbank (nur für einzelne Tasks, nicht für Batch-Operationen)
  const loadAssignedStudents = useCallback(async () => {
    // Nur laden wenn es sich um einen einzelnen Task handelt (nicht bei Batch-Operationen)
    if (!currentTaskData?.uuid || !user || !planMetadata.planName || Array.isArray(existingStudents)) {
      setDbAssignedStudents([]);
      return;
    }

    setLoadingAssignedStudents(true);
    try {
      const result = await fetchTaskAssignments(
        user?.username || user?.login || user?.id,
        currentTaskData.uuid,
        planMetadata.planVersion || 1
      );

      if (result.success && result.assignments) {
        // Transformiere die DB-Daten in das Frontend-Format
        const transformedStudents = result.assignments.map(assignment => ({
          id: assignment.login, // Login als ID verwenden
          login: assignment.login,
          vorname: assignment.vorname,
          nachname: assignment.nachname,
          schuljahrId: assignment.schuljahrId,
          klasse: assignment.klasse,
          klassenId: assignment.klassenId
        }));
        setDbAssignedStudents(transformedStudents);
      } else {
        console.warn('Fehler beim Laden der Task-Zuordnungen:', result.error);
        setDbAssignedStudents([]);
      }
    } catch (error) {
      console.error('Fehler beim Laden der zugewiesenen Lernenden:', error);
      setDbAssignedStudents([]);
    } finally {
      setLoadingAssignedStudents(false);
    }
  }, [currentTaskData?.uuid, user, planMetadata.planName, planMetadata.planVersion, existingStudents]);

  // Verwende DB-Daten wenn verfügbar und es ein einzelner Task ist, sonst Fallback zu existingStudents
  const actualExistingStudents = (dbAssignedStudents.length > 0 && currentTaskData?.uuid) ? dbAssignedStudents : existingStudents;

  useEffect(() => {
    if (show) {
      loadSchuljahre();
      loadAssignedStudents(); // Lade zugewiesene Lernende aus DB
      
      // Wenn keine existierenden Studenten vorhanden sind, automatisch auf "add" Modus wechseln
      if (actualExistingStudents.length === 0 && actionMode === 'remove') {
        setActionMode('add');
      }
    }
  }, [show, actualExistingStudents.length, actionMode, loadAssignedStudents]);

  useEffect(() => {
    if (selectedSchuljahr) {
      loadKlassen(selectedSchuljahr);
    }
  }, [selectedSchuljahr]);

  const loadLernende = useCallback(async () => {
    if (!selectedSchuljahr) return;
    
    setLernendeLoading(true);
    
    try {
      let lernendeData;
      
      // Im Entfernen-Modus nur bereits zugewiesene Lernende anzeigen
      if (actionMode === 'remove') {
        // Verwende die aktuellen DB-Daten, nicht die Props
        lernendeData = actualExistingStudents;
      } else {
        // Entscheiden welche API zu verwenden basierend auf Klassen-Auswahl
        if (selectedKlasse) {
          // Lernende für spezifische Klasse laden
          lernendeData = await fetchLernendeByKlassenId(selectedKlasse);
        } else {
          // Alle Lernende für Schuljahr laden
          lernendeData = await fetchLernendeBySchuljahrId(selectedSchuljahr);
        }
        
        if (lernendeData.error) {
          setLernende([]);
          return;
        }
      }
      
      // Filtern basierend auf Vorname und Nachname (falls nicht über Klasse gefiltert)
      let filteredLernende = lernendeData;
      
      if (searchVorname) {
        filteredLernende = filteredLernende.filter(l => 
          l.vorname && l.vorname.toLowerCase().includes(searchVorname.toLowerCase())
        );
      }
      
      if (searchNachname) {
        filteredLernende = filteredLernende.filter(l => 
          l.nachname && l.nachname.toLowerCase().includes(searchNachname.toLowerCase())
        );
      }
      
      setLernende(filteredLernende);
      
    } catch (err) {
      setLernende([]);
    } finally {
      setLernendeLoading(false);
    }
  }, [selectedSchuljahr, selectedKlasse, actionMode, actualExistingStudents, searchVorname, searchNachname]);

  useEffect(() => {
    loadLernende();
    // Klassen-Auswahl zurücksetzen wenn wir in den Entfernen-Modus wechseln
    if (actionMode === 'remove') {
      setSelectedKlasse('');
    }
  }, [selectedSchuljahr, searchVorname, searchNachname, selectedKlasse, actionMode, loadLernende]);

  const loadSchuljahre = async () => {
    setLoading(true);
    setError('');
    
    try {
      const data = await fetchSchuljahre();
      
      if (data.error) {
        setError(data.error);
      } else {
        setSchuljahre(data);
        // Aktuelles Schuljahr als Standard auswählen
        const currentSchuljahr = data.find(jahr => jahr.isCurrent === 1);
        if (currentSchuljahr) {
          setSelectedSchuljahr(currentSchuljahr.SchuljahrID.toString());
        }
      }
    } catch (err) {
      setError('Fehler beim Laden der Schuljahre');
    } finally {
      setLoading(false);
    }
  };

  const loadKlassen = async (schuljahrId) => {
    setKlassesLoading(true);
    
    try {
      const data = await fetchKlassen(schuljahrId);
      
      if (data.error) {
        setKlassen([]);
      } else {
        setKlassen(data);
      }
    } catch (err) {
      setKlassen([]);
    } finally {
      setKlassesLoading(false);
    }
  };

  const handleSchuljahrChange = (event) => {
    const newSchuljahrId = event.target.value;
    setSelectedSchuljahr(newSchuljahrId);
    
    // Suchfelder und Auswahl zurücksetzen
    setSearchVorname('');
    setSearchNachname('');
    setSelectedKlasse('');
    setSelectedLernende(new Set());
    setSortConfig({ key: null, direction: 'asc' });
    setLastSelectedIndex(null);
    setActionMode('add'); // Modus zurücksetzen
    setSubmitError(''); // Fehlermeldungen zurücksetzen
    
    // Klassen laden, wenn ein Schuljahr ausgewählt wurde
    if (newSchuljahrId) {
      loadKlassen(newSchuljahrId);
    } else {
      setKlassen([]);
      setLernende([]);
    }
  };

  const handleLernenderSelect = (lernenderId, isSelected) => {
    const newSelected = new Set(selectedLernende);
    if (isSelected) {
      newSelected.add(lernenderId);
    } else {
      newSelected.delete(lernenderId);
    }
    setSelectedLernende(newSelected);
  };

  const handleLernenderClick = (lernenderId, index, event) => {
    if (event.shiftKey && lastSelectedIndex !== null) {
      // Bereichsauswahl mit Shift-Taste
      const sortedLernende = getSortedLernende();
      const startIndex = Math.min(lastSelectedIndex, index);
      const endIndex = Math.max(lastSelectedIndex, index);
      
      const newSelected = new Set(selectedLernende);
      
      // Alle Lernende im Bereich auswählen oder abwählen
      // Entscheidung basierend auf dem Zustand des geklickten Elements
      const shouldSelect = !selectedLernende.has(lernenderId);
      
      for (let i = startIndex; i <= endIndex; i++) {
        if (shouldSelect) {
          newSelected.add(sortedLernende[i].id);
        } else {
          newSelected.delete(sortedLernende[i].id);
        }
      }
      
      setSelectedLernende(newSelected);
    } else {
      // Normale Einzelauswahl
      const isSelected = !selectedLernende.has(lernenderId);
      handleLernenderSelect(lernenderId, isSelected);
      setLastSelectedIndex(index);
    }
  };

  const handleInverseSelection = () => {
    // Inverse Auswahl: Alle ausgewählten abwählen, alle nicht-ausgewählten auswählen
    const newSelected = new Set();
    lernende.forEach(lernender => {
      if (!selectedLernende.has(lernender.id)) {
        newSelected.add(lernender.id);
      }
    });
    setSelectedLernende(newSelected);
    setLastSelectedIndex(null); // Reset bei inverse Auswahl
  };

  const handleUebernehmen = () => {
    // Reset submit error and success message
    setSubmitError('');
    
    if (selectedLernende.size === 0) {
      const actionText = actionMode === 'add' ? 'hinzufügen' : 'entfernen';
      setSubmitError(`Bitte wählen Sie mindestens einen Lernenden zum ${actionText} aus.`);
      return;
    }

    // Wenn onUebernehmen Callback verfügbar ist, die ausgewählten Lernenden übergeben
    if (onUebernehmen) {
      const selectedLernendeArray = Array.from(selectedLernende);
      
      // Erweitere die Lernenden-Daten um schuljahrId
      const lernendeWithSchuljahr = lernende.map(lernender => ({
        ...lernender,
        schuljahrId: selectedSchuljahr
      }));
      
      onUebernehmen(selectedLernendeArray, lernendeWithSchuljahr, actionMode);
    } else {
      // Fallback: Ursprüngliche Implementierung
      // Modal schließen
      onClose();
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedLernende = () => {
    if (!sortConfig.key) return lernende;
    
    return [...lernende].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';
      
      if (sortConfig.direction === 'asc') {
        return aValue.localeCompare(bValue, 'de', { sensitivity: 'base' });
      } else {
        return bValue.localeCompare(aValue, 'de', { sensitivity: 'base' });
      }
    });
  };

  // eslint-disable-next-line no-unused-vars
  // eslint-disable-next-line no-unused-vars
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  };

  if (!show) return null;

  // Footer-Buttons, damit sie bei kleinem Bildschirm im scrollbaren Inhalt angezeigt werden können
  const modalFooter = (
    <div style={{
      display: 'flex',
      gap: '8px',
      justifyContent: 'space-between',
      marginTop: '16px',
      paddingTop: '16px',
      borderTop: '1px solid #eee',
      flexShrink: 0,
      alignItems: 'center'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button
          style={{
            padding: '8px 16px',
            border: actionMode === 'add' ? '1px solid #007acc' : '1px solid #dc3545',
            borderRadius: '6px',
            backgroundColor: actionMode === 'add' ? '#007acc' : '#dc3545',
            color: 'white',
            cursor: 'pointer',
            fontSize: '14px',
            flexShrink: 0
          }}
          onClick={handleUebernehmen}
        >
          {actionMode === 'add' ? 'Hinzufügen' : 'Entfernen'}
        </button>
        {submitError && (
          <div style={{
            color: '#d32f2f',
            fontSize: '12px',
            whiteSpace: 'nowrap'
          }}>
            {submitError}
          </div>
        )}
      </div>
      <button
        style={{
          padding: '8px 16px',
          border: '1px solid #ccc',
          borderRadius: '6px',
          backgroundColor: 'white',
          cursor: 'pointer',
          fontSize: '14px'
        }}
        onClick={onClose}
      >
        Schließen
      </button>
    </div>
  );

  // Modal über Portal rendern, um sicherzustellen, dass es über allen anderen Elementen liegt
  return createPortal(
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      zIndex: 1000000,
      paddingTop: '5px',
      paddingBottom: '5px',
      paddingLeft: '5px',
      paddingRight: '5px'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: 'clamp(16px, 3vh, 24px)',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        minWidth: 'min(500px, calc(100vw - 20px))',
        maxWidth: 'min(900px, 95vw)',
        height: 'calc(100vh - 60px)',
        minHeight: '200px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        zIndex: 1000001
      }}>
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            color: '#666',
            borderRadius: '4px'
          }}
          title="Schließen"
        >
          <FiX size={20} />
        </button>

        {/* Modal Header */}
        <h3 style={{ 
          margin: '0 0 16px 0', 
          fontSize: '18px', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          paddingRight: '40px' // Platz für Close Button
        }}>
          <FiUsers />
          Lernende verwalten
        </h3>
        
        {/* Modal Content */}
        <div style={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
          fontSize: '14px',
          minHeight: '0'
        }}>
          {/* Schuljahr-Auswahl */}
          <div style={{ marginBottom: 'clamp(12px, 3vh, 24px)' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '8px'
            }}>
              <label style={{
                fontWeight: '500',
                color: '#333',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center'
              }}>
                <FiCalendar style={{ marginRight: '8px' }} />
                Schuljahr:</label>
              
              {!loading && !error && (
                <select
                  value={selectedSchuljahr}
                  onChange={handleSchuljahrChange}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">-- Schuljahr wählen --</option>
                  {schuljahre.map((jahr) => (
                    <option key={jahr.SchuljahrID} value={jahr.SchuljahrID.toString()}>
                      {jahr.Name} 
                      {jahr.isCurrent === 1 && ' (Aktuell)'}
                    
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            {loading && (
              <div style={{ color: '#666', fontStyle: 'italic' }}>
                Lade Schuljahre...
              </div>
            )}
            
            {error && (
              <div style={{ 
                color: '#d32f2f', 
                backgroundColor: '#ffebee', 
                padding: '8px', 
                borderRadius: '4px', 
                marginBottom: '12px',
                fontSize: '13px'
              }}>
                {error}
              </div>
            )}
            
            
          </div>
          
          {/* Aktions-Modus und Infobox */}
          {selectedSchuljahr && (
            <div style={{ marginBottom: 'clamp(12px, 3vh, 24px)' }}>
              <div style={{
                display: 'flex',
                gap: '16px',
                alignItems: 'flex-start',
                flexWrap: 'wrap'
              }}>
                
                {/* Linke Spalte: Aktions-Modus */}
                <div style={{ flex: '0 0 300px', minWidth: '280px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px'
                  }}>
                    <span style={{ fontWeight: '500', color: '#333' }}>
                      Aktion:
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #dee2e6'
                  }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}>
                      <input
                        type="radio"
                        name="actionMode"
                        value="add"
                        checked={actionMode === 'add'}
                        onChange={(e) => setActionMode(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      <FiUserPlus size={16} style={{ color: '#28a745' }} />
                      <span style={{ color: '#28a745', fontWeight: '500' }}>
                        Lernende hinzufügen
                      </span>
                    </label>
                    
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: actualExistingStudents.length > 0 ? 'pointer' : 'not-allowed',
                      fontSize: '14px',
                      opacity: actualExistingStudents.length > 0 ? 1 : 0.5
                    }}>
                      <input
                        type="radio"
                        name="actionMode"
                        value="remove"
                        checked={actionMode === 'remove'}
                        onChange={(e) => setActionMode(e.target.value)}
                        disabled={actualExistingStudents.length === 0}
                        style={{ 
                          cursor: actualExistingStudents.length > 0 ? 'pointer' : 'not-allowed'
                        }}
                      />
                      <FiUserMinus size={16} style={{ color: '#dc3545' }} />
                      <span style={{ color: '#dc3545', fontWeight: '500' }}>
                        Lernende entfernen
                      </span>
                    </label>
                  </div>
                </div>

                {/* Rechte Spalte: Infobox */}
                <div style={{ flex: '1', minWidth: '280px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px'
                  }}>
                    <span style={{ fontWeight: '500', color: '#333' }}>
                      Hinweis:
                    </span>
                  </div>
                  
                  <div style={{
                    padding: 'clamp(8px, 2vh, 12px)',
                    backgroundColor: '#fff3cd',
                    borderRadius: '6px',
                    border: '1px solid #ffeaa7',
                    fontSize: 'clamp(12px, 1.5vw, 13px)',
                    color: '#856404',
                    lineHeight: '1.4',
                    maxWidth: '300px'
                  }}>
                    <strong>Achtung:</strong> Dieser Dialog ist für die Auswahl einzelner Lernender gedacht oder wenn es eine Gruppe von Lernenden gibt, die nicht in einer gemeinsamen Klasse eingeteilt sind.
                    <br />
                    <br />
                    Das Einteilen einer Klasse geschieht vorzugsweise über die "Klassen-Synchronisation" Eigenschaften des Tasks.
                  </div>
                </div>
              </div>
              
              {/* Info-Meldungen unter dem Grid */}
              {actualExistingStudents.length === 0 && (
                <div style={{
                  marginTop: '12px',
                  padding: '8px 12px',
                  backgroundColor: '#d1ecf1',
                  border: '1px solid #b8daff',
                  borderRadius: '4px',
                  fontSize: '13px',
                  color: '#0c5460'
                }}>
                  <strong>Info:</strong> Noch keine Lernenden zugewiesen. 
                  Wählen Sie "Hinzufügen", um Lernende zuzuweisen.
                  {loadingAssignedStudents && " (Lade zugewiesene Lernende...)"}
                </div>
              )}
              
              {actionMode === 'remove' && actualExistingStudents.length > 0 && (
                <div style={{
                  marginTop: '12px',
                  padding: '8px 12px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '4px',
                  fontSize: '13px',
                  color: '#856404'
                }}>
                  <strong>Info:</strong> Nur bereits zugewiesene Lernende werden in der Liste angezeigt. 
                  Wählen Sie die Lernenden aus, die Sie entfernen möchten.
                </div>
              )}
            </div>
          )}

          {/* Suchfelder */}
          {selectedSchuljahr && (
            <div style={{ marginBottom: 'clamp(12px, 3vh, 24px)' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px'
              }}>
                <FiSearch style={{ color: '#666' }} />
                <span style={{ fontWeight: '500', color: '#333' }}>
                  Lernende suchen:
                </span>
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: actionMode === 'remove' 
                  ? 'minmax(120px, 1fr) minmax(120px, 1fr)' 
                  : 'minmax(120px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr)',
                gap: '16px',
                alignItems: 'start'
              }}>
                {/* Vorname */}
                <div style={{ minWidth: 0 }}>
                  <input
                    type="text"
                    value={searchVorname}
                    onChange={(e) => setSearchVorname(e.target.value)}
                    placeholder="Vorname"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ccc',
                      borderRadius: '6px',
                      fontSize: '14px',
                      backgroundColor: 'white',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                {/* Nachname */}
                <div style={{ minWidth: 0 }}>
                  <input
                    type="text"
                    value={searchNachname}
                    onChange={(e) => setSearchNachname(e.target.value)}
                    placeholder="Nachname"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ccc',
                      borderRadius: '6px',
                      fontSize: '14px',
                      backgroundColor: 'white',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                {/* Klasse - nur im Hinzufügen-Modus anzeigen */}
                {actionMode === 'add' && (
                  <div style={{ minWidth: 0 }}>
                    <select
                      value={selectedKlasse}
                      onChange={(e) => setSelectedKlasse(e.target.value)}
                      disabled={klassesLoading}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: klassesLoading ? '#f5f5f5' : 'white',
                        cursor: klassesLoading ? 'wait' : 'pointer',
                        boxSizing: 'border-box'
                      }}
                    >
                      <option value="">Alle Klassen</option>
                      {klassen.map((klasse) => (
                        <option key={klasse.id} value={klasse.id.toString()}>
                          {klasse.name}
                        </option>
                      ))}
                    </select>
                    {klassesLoading && (
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#666', 
                        fontStyle: 'italic',
                        marginTop: '4px'
                      }}>
                        Lade Klassen...
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {selectedSchuljahr ? (
            <div style={{
              color: '#333',
              flex: '0 0 auto',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible',
              paddingBottom: 'clamp(8px, 2vh, 24px)',
              minHeight: '180px'
            }}>
              {/* Lernenden-Tabelle */}
              <div style={{
                flex: '0 0 auto',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible',
                minHeight: '120px'
              }}>
                <h4 style={{ 
                  margin: '0 0 12px 0', 
                  fontSize: '16px', 
                  fontWeight: '500',
                  flexShrink: 0
                }}>
                  {actionMode === 'add' 
                    ? `Verfügbare Lernende (${lernende.length})`
                    : `Zugewiesene Lernende (${lernende.length})`
                  }
                </h4>
                
                {lernendeLoading || (actionMode === 'remove' && loadingAssignedStudents) ? (
                  <Spinner />
                ) : lernende.length > 0 ? (
                  <div style={{
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    overflow: 'visible',
                    flex: '0 0 auto',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    {/* Tabellen-Header - fixiert */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '50px minmax(120px, 1fr) minmax(120px, 1fr) 108px',
                      backgroundColor: '#f5f5f5',
                      borderBottom: '1px solid #ddd',
                      fontSize: '14px',
                      fontWeight: '500',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10
                    }}>
                      <div style={{ padding: '12px 8px', textAlign: 'center' }}>
                        <button
                          onClick={handleInverseSelection}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#666',
                            ':hover': {
                              backgroundColor: '#f0f0f0'
                            }
                          }}
                          title="Auswahl umkehren"
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          <FiRefreshCcw size={16} />
                        </button>
                      </div>
                      <div 
                        style={{ 
                          padding: '12px 8px',
                          cursor: 'pointer',
                          userSelect: 'none',
                          textAlign: 'left'
                        }}
                        onClick={() => handleSort('vorname')}
                        title="Klicken zum Sortieren"
                      >
                        Vorname
                      </div>
                      <div 
                        style={{ 
                          padding: '12px 0px',
                          cursor: 'pointer',
                          userSelect: 'none',
                          textAlign: 'left'
                        }}
                        onClick={() => handleSort('nachname')}
                        title="Klicken zum Sortieren"
                      >
                        Nachname
                      </div>
                      <div 
                        style={{ 
                          padding: '12px 0px',
                          cursor: 'pointer',
                          userSelect: 'none',
                          textAlign: 'left'
                        }}
                        onClick={() => handleSort('klasse')}
                        title="Klicken zum Sortieren"
                      >
                        Klasse
                      </div>
                    </div>
                    
                    {/* Tabellen-Inhalt */}
                    <div style={{
                      flex: '0 0 auto',
                      overflowY: 'visible',
                      overflowX: 'hidden'
                    }}>
                      {getSortedLernende().map((lernender, index) => (
                        <div 
                          key={lernender.id}
                          onClick={(e) => handleLernenderClick(lernender.id, index, e)}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '50px minmax(120px, 1fr) minmax(120px, 1fr) 100px',
                            borderBottom: '1px solid #eee',
                            fontSize: '14px',
                            backgroundColor: selectedLernende.has(lernender.id) ? '#f0f8ff' : 'white',
                            cursor: 'pointer',
                            userSelect: 'none', // Verhindert Textauswahl beim Shift-Klick
                            ':hover': {
                              backgroundColor: selectedLernende.has(lernender.id) ? '#e6f3ff' : '#f9f9f9'
                            }
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = selectedLernende.has(lernender.id) ? '#e6f3ff' : '#f9f9f9';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = selectedLernende.has(lernender.id) ? '#f0f8ff' : 'white';
                          }}
                        >
                          <div style={{ padding: '12px 8px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={selectedLernende.has(lernender.id)}
                              onChange={(e) => {
                                e.stopPropagation(); // Verhindert doppelte Ausführung
                                handleLernenderSelect(lernender.id, e.target.checked);
                                setLastSelectedIndex(index); // Index merken auch bei Checkbox-Klick
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                          </div>
                          <div style={{ padding: '12px 8px', textAlign: 'left' }}>{lernender.vorname}</div>
                          <div style={{ padding: '12px 8px', textAlign: 'left' }}>{lernender.nachname}</div>
                          <div style={{ padding: '12px 8px', textAlign: 'left' }}>{lernender.klasse}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ 
                    padding: '20px', 
                    textAlign: 'center', 
                    color: '#666',
                    fontStyle: 'italic',
                    border: '1px solid #eee',
                    borderRadius: '6px',
                    backgroundColor: '#fafafa'
                  }}>
                    {actionMode === 'add' 
                      ? 'Keine Lernende gefunden'
                      : 'Keine Lernende zugewiesen'
                    }
                  </div>
                )}
              </div>
              
              {/* Status-Informationen - fixiert unten */}
              <div style={{ 
                flexShrink: 0,
                paddingTop: '12px'
              }}>
                {/* Aktive Filter anzeigen */}
                {(searchVorname || searchNachname || (actionMode === 'add' && selectedKlasse)) && (
                  <div style={{ 
                    marginBottom: '8px', 
                    padding: '8px', 
                    backgroundColor: '#f0f8ff', 
                    borderRadius: '4px',
                    fontSize: '13px',
                    color: '#666'
                  }}>
                    Aktive Filter: 
                    {searchVorname && ` Vorname: "${searchVorname}"`}
                    {searchNachname && ` Nachname: "${searchNachname}"`}
                    {actionMode === 'add' && selectedKlasse && ` Klasse: "${klassen.find(k => k.id.toString() === selectedKlasse)?.name}"`}
                  </div>
                )}
                
                {/* Ausgewählte Lernende anzeigen */}
                {selectedLernende.size > 0 && (
                  <div style={{ 
                    padding: '8px', 
                    backgroundColor: '#e8f5e8', 
                    borderRadius: '4px',
                    fontSize: '13px',
                    color: '#2e7d32'
                  }}>
                    {selectedLernende.size} Lernende ausgewählt
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ 
              color: '#666', 
              fontStyle: 'italic',
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              Bitte wählen Sie zuerst ein Schuljahr aus.
            </div>
          )}

        </div>

        {/* Modal Footer - bleibt am unteren Rand sichtbar */}
        {modalFooter}
      </div>
    </div>,
    document.body // Modal direkt im body rendern, außerhalb der normalen DOM-Hierarchie
  );
};

export default SchuelerModal;